<template>
  <div class="view-container">
    <Loader v-if="loading" />

    <div class="doctors-container">
      <div class="sub-header">
        <h2 class="page-title">لیست اطلاعات پزشکان</h2>
        <BaseButton
          title="جدید"
          variant="primary"
          type="button"
          @click="openModal"
          id="responsive-style"
        />
      </div>
      <hr class="divider-line" />

      <div class="search">
        <input
          id="general-search"
          type="text"
          v-model="searchQuery"
          @input="fetchDoctors()"
          placeholder="جستجو..."
        />
        <BaseButton
          title="جستجو"
          variant="primary"
          type="button"
          class="search-button"
          @click="fetchDoctors()"
        />
      </div>

      <Teleport to="body">
        <div
          v-if="floatingTooltip.visible"
          class="floating-tooltip"
          :style="{ left: floatingTooltip.left + 'px', top: floatingTooltip.top + 'px' }"
        >
          {{ floatingTooltip.text }}
        </div>
      </Teleport>

      <BaseTable
        :items="filteredDoctorsList"
        keyField="id"
        :emptyColspan="9"
        :emptyText="'موردی برای نمایش وجود ندارد.'"
      >
        <template #head>
          <tr>
            <th>ردیف</th>
            <th>نام و نام خانوادگی</th>
            <th>شماره نظام پزشکی</th>
            <th>تخصص</th>
            <th>پیش فرض</th>
            <th>درصد</th>
            <th>تلفن همراه</th>
            <th>تلفن ثابت</th>
            <th>عملیات</th>
          </tr>
        </template>

        <template #row="{ item: doctor, index }">
          <td data-label="ردیف">
            {{
              toPersianDigits(
                (pagination.current_page - 1) * pagination.per_page + index + 1
              )
            }}
          </td>
          <td data-label="نام و نام خانوادگی">{{ doctor.fDname }}</td>
          <td data-label="شماره نظام پزشکی">{{ toPersianDigits(doctor.fDnezamno) }}</td>
          <td data-label="تخصص">{{ doctor.fDtakhasos }}</td>
          <td data-label="پیش فرض">{{ hasDefault(doctor.isDefault) }}</td>
          <td data-label="درصد">
            {{ toPersianDigits(doctor.fDpercent ? doctor.fDpercent + '%' : '') }}
          </td>
          <td data-label="تلفن همراه">{{ toPersianDigits(doctor.fDmobile) }}</td>
          <td data-label="تلفن ثابت">{{ toPersianDigits(doctor.fDtele) }}</td>
          <td data-label="عملیات" class="actions-cell">
            <BaseButton
              variant="ghost"
              type="button"
              class="action-btn edit"
              aria-label="ویرایش"
              @click.stop="handleEdit(doctor)"
              @mouseenter.stop="showFloatingTooltip($event, 'ویرایش')"
              @mouseleave.stop="hideFloatingTooltip()"
              @focus.stop="showFloatingTooltip($event, 'ویرایش')"
              @blur.stop="hideFloatingTooltip()"
            >
              <template #icon>
                <img src="/img/edit.svg" alt="ویرایش" />
              </template>
            </BaseButton>
            <BaseButton
              variant="ghost"
              type="button"
              class="action-btn group"
              aria-label="تعیین گروه"
              @click.stop="openGroupModal(doctor)"
              @mouseenter.stop="showFloatingTooltip($event, 'تعیین گروه')"
              @mouseleave.stop="hideFloatingTooltip()"
              @focus.stop="showFloatingTooltip($event, 'تعیین گروه')"
              @blur.stop="hideFloatingTooltip()"
            >
              <template #icon>
                <img src="/img/group.svg" alt="تعیین گروه" />
              </template>
            </BaseButton>
            <BaseButton
              variant="ghost"
              type="button"
              class="action-btn delete"
              aria-label="حذف"
              @click.stop="deleteDoctorHandler(doctor.fDauto)"
              @mouseenter.stop="showFloatingTooltip($event, 'حذف')"
              @mouseleave.stop="hideFloatingTooltip()"
              @focus.stop="showFloatingTooltip($event, 'حذف')"
              @blur.stop="hideFloatingTooltip()"
            >
              <template #icon>
                <img src="/img/trash.svg" alt="حذف" />
              </template>
            </BaseButton>
          </td>
        </template>
      </BaseTable>
    </div>

    <!-- Modal -->
    <div v-if="isModalOpen" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-sub-header">
          <div class="sub-header">
            <ModalHeader>
              <template #default>
                <h2 class="page-title">
                  {{ formMode === 'add' ? 'افزودن پزشک جدید' : 'ویرایش اطلاعات پزشک' }}
                </h2>
              </template>
            </ModalHeader>
            <div class="form-actions">
              <BaseButton
                title="ثبت اطلاعات"
                variant="primary"
                type="submit"
                form="doctor-form"
                id="responsive-style"
              />
              <BaseButton
                title="انصراف"
                variant="cancel"
                @click="closeModal"
                id="responsive-style"
              />
            </div>
          </div>
          <hr class="divider-line" />
        </div>

        <form @submit.prevent="handleSubmit" class="doctor-form-container" id="doctor-form">
          <div class="top-section">
            <div class="form-group span-4">
              <label class="checkbox-label">
                <input type="checkbox" v-model="doctorForm.isDefault" />
                پزشک پیش‌فرض
              </label>
            </div>

            <div class="top-form-fields">
              <div class="form-group">
                <label>نام و نام خانوادگی <span class="required">*</span></label>
                <input type="text" v-model="doctorForm.fDname" required />
              </div>
              <div class="form-group">
                <label>شماره نظام پزشکی <span class="required">*</span></label>
                <input type="text" v-model="doctorForm.fDnezamno" required />
              </div>
              <div class="form-group">
                <label>درصد<span class="required">*</span></label>
                <input type="number" v-model="doctorForm.fDpercent" required />
              </div>
            </div>

            <div class="bottom-form-fields">
              <div class="form-group">
                <label>تخصص</label>
                <input type="text" v-model="doctorForm.fDtakhasos" />
              </div>
              <div class="form-group">
                <label>تلفن همراه</label>
                <input type="tel" v-model="doctorForm.fDmobile" />
              </div>
              <div class="form-group">
                <label>تلفن ثابت</label>
                <input type="tel" v-model="doctorForm.fDtele" />
              </div>
            </div>

            <div class="form-group span-4">
              <label>آدرس</label>
              <textarea v-model="doctorForm.fDaddr" rows="2"></textarea>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Group assignment modal -->
    <div v-if="isGroupModalOpen" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-sub-header">
          <div class="sub-header">
            <ModalHeader>
              <template #default>
                <h2 class="page-title">تعیین گروه پزشک</h2>
              </template>
            </ModalHeader>
            <div class="form-actions">
              <BaseButton title="ثبت" variant="primary" type="button" @click="assignDoctorGroup" />
              <BaseButton title="انصراف" variant="cancel" @click="closeGroupModal" />
            </div>
          </div>
          <hr class="divider-line" />
        </div>

        <div class="modal-body" style="padding: 10px;">
          <div class="form-group">
            <div class="groups-list">
              <label v-for="(g, idx) in doctorGroups" :key="idx" class="checkbox-item">
                <input
                  class="checkbox-input"
                  type="checkbox"
                  :value="g"
                  v-model="selectedGroups"
                />
                <span class="checkbox-text">{{ g }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useFloatingTooltip } from '@/composables/useFloatingTooltip'
import { storeToRefs } from "pinia";
import { useDoctorsStore } from "@/stores/clinic/doctors.js";
import Loader from '@/components/Loader.vue';
import ModalHeader from '@/components/ModalHeader.vue';
import BaseButton from '@/components/UiCompunents/BaseButton.vue';
import BaseTable from '@/components/UiCompunents/BaseTable.vue';
import { toPersianDigits } from '@/utils/formatters.js';

const isModalOpen = ref(false);
const formMode = ref("add");

const doctorsStore = useDoctorsStore();
const { doctorsList, pagination, searchQuery, loading } = storeToRefs(doctorsStore);
const { addDoctor, updateDoctor, deleteDoctor, fetchDoctors } = doctorsStore;

const initialFormState = {
  fDname: "",
  fDnezamno: "",
  fDtakhasos: "",
  fDmobile: "",
  fDtele: "",
  fDaddr: "",
  fDpercent: 0,
  fDauto: 0,
  isdefault: false,
};
let doctorForm = reactive({ ...initialFormState });

// --- Group modal state ---
const isGroupModalOpen = ref(false);
const selectedDoctor = ref(null);
// Allow multiple groups (checkboxes)
const selectedGroups = ref([]);
// fallback list of groups; can be replaced by fetching or a shared store
const doctorGroups = ref([
  "مدیریت کلینیک",
  "گروه قلب و عروق",
  "گروه زنان و زایمان",
  "گروه اطفال",
]);

// جدول مستقیم از store
const filteredDoctorsList = computed(() => doctorsList.value);

function resetForm() {
  Object.assign(doctorForm, initialFormState);
}

// toPersianDigits imported from utils/formatters
function hasDefault(data) {
  return data ? "بلی" : "خیر"
}
function openModal() {
  formMode.value = "add";
  resetForm();
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
}

function handleEdit(doctor) {
  formMode.value = "edit";
  Object.assign(doctorForm, doctor);
  isModalOpen.value = true;
}

async function handleSubmit() {
  if (formMode.value === "add") {
    await addDoctor({ ...doctorForm });
  } else {
    await updateDoctor({ ...doctorForm });
    await fetchDoctors(pagination.value.current_page); // جدول را بروزرسانی کن
  }
  resetForm();
  closeModal();
}

// دکمه حذف با بروزرسانی جدول
async function deleteDoctorHandler(doctorId) {
  try {
    await deleteDoctor(doctorId);
    await fetchDoctors(pagination.value.current_page);
  } catch (err) {
    console.error(err);
    alert("خطا در حذف پزشک");
  }
}

// Group modal handlers
function openGroupModal(doctor) {
  selectedDoctor.value = doctor;
  // Normalize existing value: could be string (csv) or array
  const existing = doctor.medicalGroup || doctor.fDgroup || "";
  if (Array.isArray(existing)) {
    selectedGroups.value = [...existing];
  } else if (typeof existing === 'string' && existing.trim() !== '') {
    // split by comma if it is a comma-separated list
    selectedGroups.value = existing.split(',').map(s => s.trim()).filter(Boolean);
  } else {
    selectedGroups.value = [];
  }
  isGroupModalOpen.value = true;
}

function closeGroupModal() {
  isGroupModalOpen.value = false;
  selectedDoctor.value = null;
  selectedGroups.value = [];
}

async function assignDoctorGroup() {
  if (!selectedDoctor.value) return;
  try {
    // update locally first for instant UI feedback
    const index = doctorsList.value.findIndex(d => d.fDauto === selectedDoctor.value.fDauto);
    const newGroups = Array.isArray(selectedGroups.value) ? [...selectedGroups.value] : [];
    // store a CSV-like string for legacy fields; keep medicalGroup as string to avoid breaking other UI
    const fDgroupString = newGroups.join(', ');
    if (index > -1) {
      doctorsList.value[index] = { ...doctorsList.value[index], medicalGroup: fDgroupString, fDgroup: fDgroupString };
    }
    // attempt to update backend; send medicalGroup as string for compatibility
    await updateDoctor({ ...selectedDoctor.value, medicalGroup: fDgroupString, fDgroup: fDgroupString });
    await fetchDoctors(pagination.value.current_page);
  } catch (err) {
    console.error(err);
    alert("خطا در ثبت گروه پزشک");
  } finally {
    closeGroupModal();
  }
}

onMounted(() => {
  fetchDoctors();
});

const { floatingTooltip, showFloatingTooltip, hideFloatingTooltip } = useFloatingTooltip()
</script>

<style scoped>
.view-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.header {
  width: 100%;
  text-align: right;
  color: var(--text-color-primary);
  padding: 10px;

  border-radius: 20px;
}

.modal-sub-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: var(--background-color);
  gap: 10px;
  z-index: 99;
  padding-top: 20px;
}

.sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: var(--text-color-secondary);
  width: 100%;

  .add {
    display: flex;
    align-items: center;
    padding: 10px 22px;
    border-radius: 10px;
    border: none;
    cursor: pointer;
    background-color: var(--background-color-button);
    color: var(--background-color);

    &:hover {
      background-color: var(--background-color-button-hover);
    }
  }
}

.doctors-container {
  direction: rtl;
  background-color: var(--background-color);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 20px;
}

/* The main form container is now a flexbox column */
.doctor-form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* Gap between top and bottom sections */
}

.top-form-fields {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

/* --- End of new styles --- */

.top-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bottom-form-fields {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 5px;
  color: var(--tex-color-thirdly);
  text-align: justify;
}

.required {
  color: #ff0f0f;
  margin-right: 4px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 5px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  width: 100%;
  background-color: var(--background-color-secondary);
  color: var(--text-color-primary);
}

.form-group input,
.form-group select {
  height: 34px;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--text-color-primary);
}

.form-actions {
  display: flex;
  justify-content: flex-start;
}

.p-date {
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  overflow: hidden;
  background-color: var(--background-color);
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.p-date :deep(.vpd-container) {
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  border-radius: 0.75rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  background-color: var(--background-color);
}

.p-date :deep(.vpd-input-group label) {
  background-color: var(--background-color-button) !important;
}

.p-date :deep(.vpd-input-group input) {
  border: none !important;
  line-height: 32px !important;
  background-color: var(--background-color) !important;
  color: var(--text-color-primary) !important;
}

.p-date :deep(.vpd-input-group input::placeholder) {
  color: var(--text-color-secondary) !important;
  opacity: 1;
}

.p-date :deep(.vpd-input-group input:focus) {
  border: 1px solid transparent !important;
}

.p-date :deep(.vpd-icon-btn),
.p-date :deep(button.vpd-icon-btn),
.p-date :deep(.vpd-actions button) {
  background-color: var(--background-color-button) !important;
  color: var(--background-color) !important;
  transition: background-color 0.3s ease !important;
}

.p-date :deep(.vpd-icon-btn svg),
.p-date :deep(button.vpd-icon-btn svg),
.p-date :deep(.vpd-actions button svg),
.p-date :deep(.vpd-icon-btn svg path),
.p-date :deep(button.vpd-icon-btn svg path),
.p-date :deep(.vpd-actions button svg path) {
  fill: var(--background-color) !important;
  color: var(--background-color) !important;
  stroke: var(--background-color) !important;
}

.p-date :deep(.vpd-icon-btn:hover),
.p-date :deep(button.vpd-icon-btn:hover),
.p-date :deep(.vpd-actions button:hover) {
  background-color: var(--background-color-button-hover) !important;
}

/* submit-button replaced by BaseButton (variant="primary") */



input#general-search {
  width: auto;
  flex: 1;
  padding: 6px 8px;
  border: 1px solid var(--text-color-secondary);
  border-radius: 10px;
  text-align: start;
  background-color: var(--background-color);
  color: var(--text-color-primary);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

input#general-search:focus {
  outline: none;
  border-color: var(--text-color-primary);
  box-shadow: 0 0 0 2px rgba(var(--background-color-button-rgb), 0.12);
}

input#general-search::placeholder {
  color: var(--text-color-secondary);
  opacity: 1;
}

.search {
  display: flex;
  gap: 8px;
  align-items: center;
}

.search-button {
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  background-color: var(--background-color-button);
  color: var(--background-color);
  cursor: pointer;
}

.search-button:hover {
  background-color: var(--background-color-button-hover);
}

.search > input#general-search {
  flex: 1;
}


.doctors-table {
  width: 100%;
  text-align: right;
  font-size: calc(0.3vw + 8px);
  color: var(--text-color-primary);
  /* FIX 1: Change border-collapse and add border-spacing */
  border-collapse: separate;
  border-spacing: 0;
}

.doctors-table th,
.doctors-table td {
  padding: 12px 15px;
  border-bottom: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  white-space: nowrap;
}

.actions-cell .action-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  margin-left: 6px;
}
.actions-cell .action-btn img {
  width: 18px;
  height: 18px;
}
.action-btn.group img {
  width: 20px;
  height: 20px;
}

.groups-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
/* removed old radio-item; using checkbox-item styles below */

/* New layout: right aligned radio items with a small area for the radio and more space for the label */
.groups-list {
  /* ensure RTL alignment inside the groups list */
  direction: rtl;
}
.checkbox-item {
  display: flex;
  align-items: center;
  justify-content: flex-end; /* push items to the right side of the container */
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
}
.checkbox-item:hover {
  background-color: rgba(var(--text-color-secondary-rgb), 0.03);
}
.checkbox-item .checkbox-input {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  -webkit-appearance: checkbox;
  -moz-appearance: checkbox;
  appearance: checkbox;
  accent-color: var(--background-color-button);
  transform: none;
  margin-left: 8px; /* small gap between checkbox and text */
}
.checkbox-item .checkbox-text {
  flex: 1 1 auto; /* let label take remaining space */
  text-align: right; /* right align text for RTL */
  padding-right: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.doctors-table td {
  border-left: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
}

.doctors-table td:last-child {
  border-left: none;
}

.doctors-table thead {
  background-color: var(--hover-background-color);
  z-index: 2;
}

.doctors-table th {
  /* This makes the header sticky */
  position: sticky;
  top: 0;
  z-index: 2;
  font-weight: bold;
  border-left: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  background-color: var(--hover-background-color);
}

.doctors-table th:last-child {
  border-left: none;
}

/* This makes the LAST column sticky */
.doctors-table th:last-child,
.doctors-table td:last-child {
  position: sticky;
  left: 0;
  /* Sticks to the left edge */
  z-index: 1;
  box-shadow: 10px 0px 10px -5px rgba(var(--text-color-secondary-rgb), 0.25);
}

.doctors-table th:last-child {
  background-color: var(--hover-background-color);
}

.doctors-table td:last-child {
  background-color: var(--hover-background-color);
}

/* FIX 2: Corrected the selector typo (added the dot) */
/* This ensures the top-left corner is always on top */
.doctors-table th:last-child {
  z-index: 3;
}

.doctors-table tbody tr:hover {
  background-color: rgba(var(--text-color-secondary-rgb), 0.06);
}

.empty-row td {
  text-align: center;
  padding: 40px;
  color: var(--text-color-secondary);
}

.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  margin: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: calc(0.2vw + 14px);
  height: 100%;
  min-height: 32px;
  width: 100%;

  .edit {
    color: #1476f3;
  }

  .delete {
    color: #ff0f0f;
  }
}

/* Inline tooltip CSS removed — floating tooltip used instead */

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100%;
  background-color: rgba(var(--text-color-secondary-rgb), 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  -webkit-backdrop-filter: saturate(180%) blur(5px) !important;
  backdrop-filter: saturate(180%) blur(5px) !important;
}

.modal-content {
  background-color: var(--background-color);
  padding: 0 20px 20px 20px;
  border-radius: 30px;
  width: auto;
  max-height: 90vh;
  max-width: 90vw;
  overflow-y: auto;
  position: relative;
  direction: rtl;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 10px;
}

.modal-close-btn {
  border: none;
  font-size: calc(0.3vw + 12px);
  color: var(--text-color-secondary);
  background-color: transparent;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-weight: normal;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.checkbox-group {
  display: flex;
  align-items: flex-end;
}

.form-group.span-4 {
  grid-column: 1 / -1;
}

@media (max-width: 768px) {
  .doctors-container {
    margin-bottom: 0px !important;
  }

  .add {
    padding: 5px 18px !important;
  }

  .modal-overlay {
    align-items: end !important;
  }

  .modal-content {
    max-width: 95vw !important;
    width: 100% !important;
    max-height: 80vh !important;
    padding: 0 10px 10px 10px !important;
    border-radius: 20px 20px 0 0 !important;
    margin: 0 !important;
  }

  .modal-sub-header {
    padding-top: 10px !important;
  }

  .top-form-fields,
  .bottom-form-fields {
    grid-template-columns: 1fr;
  }

  .form-group.span-4 {
    grid-column: span 1;
  }

  .image-upload-area {
    min-height: 200px;
  }

  /* submit-button replaced by BaseButton (variant="primary") */


  .doctors-table thead {
    /* 1. Hide the table header on mobile */
    display: none;
  }

  .doctors-table tr {
    /* 2. Make each row a card */
    display: block;
    border: 1px solid var(--tex-color-thirdly) !important;
    border-radius: 8px;
    margin-bottom: 1rem;
    padding: 0.5rem;
  }

  .doctors-table td {
    /* 3. Make each cell a full-width row inside the card */
    display: flex;
    justify-content: space-between;
    /* Puts label on right, value on left */
    align-items: center;
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid #f0f0f0;
    text-align: left;
    /* Align data to the left */
    white-space: normal;
    /* Allow text to wrap */
  }

}

</style>

